import React, { useMemo, useState } from 'react';
import * as d3 from 'd3-geo';
import { bandColor, fmt } from '../../lib/format.js';

/**
 * IndiaChoropleth — GeoJSON-based (d3-geo path).
 * Names in `stateStatsByName` should match the canonical names in the GeoJSON
 * (Andaman & Nicobar / Jammu & Kashmir / Odisha / Uttarakhand etc.).
 */
export default function IndiaChoropleth({ geojson, stateStatsByName }) {
  const [hover, setHover] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const width = 640, height = 720;

  const path = useMemo(() => {
    if (!geojson) return null;
    // geoIdentity with reflectY(true) treats the coords as screen units after fitting —
    // avoids the polygon-winding pitfalls of geoMercator on RFC-7946 GeoJSON and
    // looks near-identical to Mercator for India (equatorial). fitSize computes
    // bounds from GeoJSON directly.
    const projection = d3.geoIdentity().reflectY(true).fitSize([width, height], geojson);
    return d3.geoPath(projection);
  }, [geojson]);

  if (!geojson) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)', fontSize: 13 }} data-testid="choropleth-loading">
        Loading map…
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }} data-testid="india-choropleth">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="India state performance map">
        {geojson.features.map((f) => {
          const name = f.properties.NAME_1 || f.properties.name;
          const stat = stateStatsByName[name];
          const fill = stat && stat.band ? bandColor(stat.band) : '#eef1f7';
          return (
            <path
              key={name}
              d={path(f)}
              fill={fill}
              stroke="#fff"
              strokeWidth="0.7"
              onMouseMove={(e) => {
                setHover({ name, stat });
                const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
                setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
              }}
              onMouseLeave={() => setHover(null)}
              style={{ transition: 'fill .2s', cursor: 'pointer' }}
            >
              <title>{name}{stat ? ` · ${fmt(stat.avgScore, 2)} (${stat.band})` : ' · no data'}</title>
            </path>
          );
        })}
      </svg>

      {hover && (
        <div style={{
          position: 'absolute', left: pos.x + 14, top: pos.y + 14, pointerEvents: 'none',
          background: '#042a54', color: '#fff', padding: '8px 12px', borderRadius: 8,
          fontSize: 12, boxShadow: 'var(--shadow-lg)', zIndex: 5, minWidth: 160,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{hover.name}</div>
          {hover.stat ? (
            <>
              <div>Projects: <b>{hover.stat.projects}</b></div>
              <div>Avg score: <b>{fmt(hover.stat.avgScore, 2)}</b></div>
              <div>Band: <b>{hover.stat.band}</b></div>
            </>
          ) : (
            <div style={{ opacity: .7 }}>No data</div>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        {['Excellent', 'Good', 'Moderate', 'Poor'].map((b) => (
          <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: 'var(--ink-2)' }}>
            <span style={{ width: 12, height: 12, background: bandColor(b), borderRadius: 3 }} />
            {b}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)' }}>
          <span style={{ width: 12, height: 12, background: '#eef1f7', borderRadius: 3, border: '1px solid var(--line)' }} />
          No data
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--ink-4)' }}>
        LGD-keyed base map · post-reorg (Telangana, Ladakh, J&amp;K as separate states). Data ODbL-1.0.
      </div>
    </div>
  );
}
